import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Funnel, FunnelChart, LabelList
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, UserPlus, Target, CheckCircle,
  Loader2, RefreshCw, ArrowRight, Briefcase
} from 'lucide-react';
import { api } from '../../lib/api';

const COLORS = {
  primary: '#0F2744',
  accent: '#4C8F6E',
  accentLight: '#6BAE8A',
  primaryLight: '#2F4477',
  primaryFaded: '#5A7BA5',
  accentFaded: '#8CB8A1',
};

const CHART_COLORS = [COLORS.primary, COLORS.accent, COLORS.primaryLight, COLORS.accentLight, COLORS.primaryFaded, COLORS.accentFaded];

const AnalyticsPage = () => {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [kpis, setKpis] = useState(null);
  const [leadFunnel, setLeadFunnel] = useState(null);
  const [candidateFunnel, setCandidateFunnel] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [leadTrends, setLeadTrends] = useState(null);
  const [talentDemand, setTalentDemand] = useState(null);

  useEffect(() => {
    fetchAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAllData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [kpisRes, leadFunnelRes, candidateFunnelRes, sourcesRes, trendsRes, demandRes] = await Promise.all([
        api.get(`/admin/analytics/kpis?period=${period}`),
        api.get(`/admin/analytics/lead-funnel?period=${period}`),
        api.get(`/admin/analytics/candidate-funnel?period=${period}`),
        api.get(`/admin/analytics/lead-sources?period=${period}`),
        api.get(`/admin/analytics/lead-trends?period=${period}&granularity=day`),
        api.get(`/admin/analytics/talent-type-demand?period=${period}`)
      ]);

      setKpis(await kpisRes.json());
      setLeadFunnel(await leadFunnelRes.json());
      setCandidateFunnel(await candidateFunnelRes.json());
      setLeadSources(await sourcesRes.json());
      setLeadTrends(await trendsRes.json());
      setTalentDemand(await demandRes.json());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const GrowthIndicator = ({ value }) => {
    const isPositive = value >= 0;
    return (
      <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-[#4C8F6E]' : 'text-[#0F2744]/60'}`}>
        {isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
        <span>{isPositive ? '+' : ''}{value}%</span>
      </div>
    );
  };

  const KPICard = ({ title, value, subValue, growth, icon: Icon, color }) => (
    <Card className="p-6 border-tp-border hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-tp-text-light">{title}</p>
          <p className="text-3xl font-display font-bold mt-1" style={{ color }}>{value}</p>
          {subValue && <p className="text-sm text-tp-text-light mt-1">{subValue}</p>}
        </div>
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-6 w-6" style={{ color }} />
        </div>
      </div>
      {growth !== undefined && (
        <div className="mt-4 pt-4 border-t border-tp-border">
          <GrowthIndicator value={growth} />
          <span className="text-xs text-tp-text-light ml-2">vs période précédente</span>
        </div>
      )}
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-tp-border">
          <p className="font-medium text-tp-text">{label}</p>
          {payload.map((entry, index) => (
            <p key={`entry-${entry.name}-${index}`} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="h-10 w-10 animate-spin text-tp-accent" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text">Analytics</h1>
          <p className="text-sm text-tp-text-light">Performances de conversion et recrutement</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="period-select">
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
              <SelectItem value="90d">90 derniers jours</SelectItem>
              <SelectItem value="1y">12 derniers mois</SelectItem>
              <SelectItem value="all">Tout</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => fetchAllData(true)} 
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Total Leads"
            value={kpis.kpis.leads.total}
            subValue={`${kpis.kpis.leads.conversion_rate}% taux de conversion`}
            growth={kpis.kpis.leads.growth}
            icon={UserPlus}
            color={COLORS.primary}
          />
          <KPICard
            title="Leads Convertis"
            value={kpis.kpis.leads.converted}
            subValue="clients acquis"
            icon={Target}
            color={COLORS.accent}
          />
          <KPICard
            title="Candidatures"
            value={kpis.kpis.candidates.total}
            subValue={`${kpis.kpis.candidates.validation_rate}% taux de validation`}
            growth={kpis.kpis.candidates.growth}
            icon={Users}
            color={COLORS.primaryLight}
          />
          <KPICard
            title="Talents en Mission"
            value={kpis.kpis.operations.active_assignments}
            subValue={`${kpis.kpis.operations.talent_pool_size} dans le pool`}
            icon={Briefcase}
            color={COLORS.accentLight}
          />
        </div>
      )}

      {/* Tabs for different views */}
      <Tabs defaultValue="funnels" className="space-y-6">
        <TabsList className="bg-tp-bg border border-tp-border">
          <TabsTrigger value="funnels" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Entonnoirs
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Tendances
          </TabsTrigger>
          <TabsTrigger value="sources" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Sources
          </TabsTrigger>
          <TabsTrigger value="demand" className="data-[state=active]:bg-tp-primary data-[state=active]:text-white">
            Demande
          </TabsTrigger>
        </TabsList>

        {/* Funnels Tab */}
        <TabsContent value="funnels" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Funnel */}
            <Card className="p-6 border-tp-border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-tp-text">Entonnoir Leads</h3>
                  <p className="text-sm text-tp-text-light">{leadFunnel?.total_leads || 0} leads total</p>
                </div>
                <Badge variant="outline" className="bg-tp-accent/10 text-tp-accent border-tp-accent">
                  {leadFunnel?.conversion_rates?.overall_conversion || 0}% conversion
                </Badge>
              </div>
              <div className="space-y-3">
                {leadFunnel?.stages?.map((stage, index) => (
                  <div key={stage.stage} className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-tp-text">{stage.label}</span>
                      <span className="text-sm text-tp-text-light">{stage.count}</span>
                    </div>
                    <div className="h-8 bg-tp-border rounded-lg overflow-hidden">
                      <div 
                        className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ 
                          width: `${Math.max(stage.percentage, 5)}%`,
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                        }}
                      >
                        <span className="text-xs font-medium text-white">{stage.percentage}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-tp-border flex items-center gap-2 text-sm text-tp-text-light">
                <span>Leads perdus:</span>
                <Badge variant="outline" className="bg-tp-error/10 text-tp-error border-tp-error">
                  {leadFunnel?.lost_leads || 0}
                </Badge>
              </div>
            </Card>

            {/* Candidate Funnel */}
            <Card className="p-6 border-tp-border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-tp-text">Entonnoir Candidats</h3>
                  <p className="text-sm text-tp-text-light">{candidateFunnel?.total_applications || 0} candidatures</p>
                </div>
                <Badge variant="outline" className="bg-tp-success/10 text-tp-success border-tp-success">
                  {candidateFunnel?.conversion_rates?.overall_success_rate || 0}% succès
                </Badge>
              </div>
              <div className="space-y-3">
                {candidateFunnel?.stages?.map((stage, index) => (
                  <div key={stage.stage} className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-tp-text">{stage.label}</span>
                      <span className="text-sm text-tp-text-light">{stage.count}</span>
                    </div>
                    <div className="h-8 bg-tp-border rounded-lg overflow-hidden">
                      <div 
                        className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ 
                          width: `${Math.max(stage.percentage, 5)}%`,
                          backgroundColor: CHART_COLORS[(index + 2) % CHART_COLORS.length]
                        }}
                      >
                        <span className="text-xs font-medium text-white">{stage.percentage}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-tp-border flex items-center gap-4 text-sm text-tp-text-light">
                <span className="flex items-center gap-2">
                  Rejetés: <Badge variant="outline" className="bg-tp-error/10 text-tp-error border-tp-error">{candidateFunnel?.rejected || 0}</Badge>
                </span>
                <span className="flex items-center gap-2">
                  Test échoué: <Badge variant="outline" className="bg-tp-warning/10 text-tp-warning border-tp-warning">{candidateFunnel?.test_failed || 0}</Badge>
                </span>
              </div>
            </Card>
          </div>

          {/* Conversion Rates Summary */}
          <Card className="p-6 border-tp-border">
            <h3 className="text-lg font-semibold text-tp-text mb-6">Taux de Conversion Détaillés</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 rounded-xl bg-tp-primary/5">
                <p className="text-3xl font-bold text-tp-primary">{leadFunnel?.conversion_rates?.lead_to_contact || 0}%</p>
                <p className="text-sm text-tp-text-light mt-1">Lead → Contact</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-tp-accent/5">
                <p className="text-3xl font-bold text-tp-accent">{leadFunnel?.conversion_rates?.contact_to_qualified || 0}%</p>
                <p className="text-sm text-tp-text-light mt-1">Contact → Qualifié</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-blue-50">
                <p className="text-3xl font-bold text-blue-600">{leadFunnel?.conversion_rates?.qualified_to_client || 0}%</p>
                <p className="text-sm text-tp-text-light mt-1">Qualifié → Client</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-green-50">
                <p className="text-3xl font-bold text-green-600">{candidateFunnel?.conversion_rates?.validation_to_assignment || 0}%</p>
                <p className="text-sm text-tp-text-light mt-1">Validé → En mission</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends">
          <Card className="p-6 border-tp-border">
            <h3 className="text-lg font-semibold text-tp-text mb-6">Évolution des Leads</h3>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={leadTrends?.data || []}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorConverted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={(value) => {
                    if (!value) return '';
                    const parts = value.split('-');
                    return parts.length >= 2 ? `${parts[2] || ''}/${parts[1]}` : value;
                  }}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="leads" 
                  name="Nouveaux Leads"
                  stroke={COLORS.primary} 
                  fill="url(#colorLeads)" 
                  strokeWidth={2}
                />
                <Area 
                  type="monotone" 
                  dataKey="converted" 
                  name="Convertis"
                  stroke={COLORS.accent} 
                  fill="url(#colorConverted)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        {/* Sources Tab */}
        <TabsContent value="sources">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 border-tp-border">
              <h3 className="text-lg font-semibold text-tp-text mb-6">Répartition par Source</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leadSources?.sources || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="source"
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                  >
                    {leadSources?.sources?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 border-tp-border">
              <h3 className="text-lg font-semibold text-tp-text mb-6">Performance par Source</h3>
              <div className="space-y-4">
                {leadSources?.sources?.map((source, index) => (
                  <div key={source.source} className="flex items-center gap-4">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-tp-text capitalize">{source.source}</span>
                        <span className="text-sm text-tp-text-light">{source.count} leads</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-tp-border rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${source.conversion_rate}%`,
                              backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                            }}
                          />
                        </div>
                        <span className="text-xs text-tp-text-light">{source.conversion_rate}% conv.</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Demand Tab */}
        <TabsContent value="demand">
          <Card className="p-6 border-tp-border">
            <h3 className="text-lg font-semibold text-tp-text mb-6">Demande par Type de Talent</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={talentDemand?.demand_by_type || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis 
                  type="category" 
                  dataKey="talent_type" 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  width={150}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="requests" name="Demandes" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                <Bar dataKey="converted" name="Convertis" fill={COLORS.accent} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
